function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cloneValue(value) {
  if (value == null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(cloneValue);
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneValue(item)]));
}

export function mergePlainObjects(base = {}, patch = {}) {
  const next = cloneValue(base) || {};

  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value === undefined) return;
    if (isPlainObject(value) && isPlainObject(next[key])) {
      next[key] = mergePlainObjects(next[key], value);
      return;
    }
    next[key] = cloneValue(value);
  });

  return next;
}

function ensureObject(parent, key) {
  if (!isPlainObject(parent[key])) {
    parent[key] = {};
  }
  return parent[key];
}

function removeIfNull(object, key) {
  if (object && object[key] === null) {
    delete object[key];
  }
}

function setOrRemove(object, key, value) {
  if (value === undefined) return;
  if (value === null) {
    delete object[key];
    return;
  }
  object[key] = value;
}

function syncOnboardingMirrors(next, patch) {
  const onboardingDetails = ensureObject(next, 'onboardingDetails');
  const patchOnboarding = patch.onboardingDetails || {};

  const currentStep =
    patch.onboardingCurrentStep !== undefined
      ? patch.onboardingCurrentStep
      : patchOnboarding.currentStep;
  setOrRemove(next, 'onboardingCurrentStep', currentStep);
  setOrRemove(onboardingDetails, 'currentStep', currentStep);

  const completedSteps =
    patch.onboardingCompletedSteps !== undefined
      ? patch.onboardingCompletedSteps
      : patchOnboarding.completedSteps;
  setOrRemove(next, 'onboardingCompletedSteps', completedSteps);
  setOrRemove(onboardingDetails, 'completedSteps', completedSteps);

  const driverFlowsCompleted =
    patch.driverFlowsCompleted !== undefined
      ? patch.driverFlowsCompleted
      : patchOnboarding.driverFlowsCompleted;
  if (driverFlowsCompleted !== undefined) {
    next.driverFlowsCompleted = mergePlainObjects(next.driverFlowsCompleted || {}, driverFlowsCompleted);
    onboardingDetails.driverFlowsCompleted = mergePlainObjects(
      onboardingDetails.driverFlowsCompleted || {},
      driverFlowsCompleted,
    );
  }

  const selectedPlan =
    patch.onboardingSelectedPlan !== undefined
      ? patch.onboardingSelectedPlan
      : patchOnboarding.selectedPlan;
  setOrRemove(next, 'onboardingSelectedPlan', selectedPlan);
  setOrRemove(onboardingDetails, 'selectedPlan', selectedPlan);

  const hasWebsite = patch.hasWebsite;
  if (hasWebsite !== undefined) {
    setOrRemove(next, 'hasWebsite', hasWebsite);
    if (hasWebsite === true) {
      next.activationPath = 'hasWebsite';
      onboardingDetails.activationPath = 'hasWebsite';
    } else if (hasWebsite === false) {
      next.activationPath = 'doesNotHaveWebsite';
      onboardingDetails.activationPath = 'doesNotHaveWebsite';
    } else if (hasWebsite === null) {
      delete next.activationPath;
      delete onboardingDetails.activationPath;
    }
  }
}

function syncPaymentProcessorMirrors(next, patch) {
  const stripeDetails = ensureObject(next, 'stripeDetails');
  const patchStripe = patch.stripeDetails || {};
  const stripeAccountID =
    patch.stripeAccountID !== undefined ? patch.stripeAccountID : patchStripe.accountId;

  setOrRemove(next, 'stripeAccountID', stripeAccountID);
  setOrRemove(stripeDetails, 'accountId', stripeAccountID);

  if (patch.defaultPaymentProcessor !== undefined) {
    setOrRemove(next, 'defaultPaymentProcessor', patch.defaultPaymentProcessor);
  }

  Object.keys(stripeDetails).forEach((key) => removeIfNull(stripeDetails, key));
  if (Object.keys(stripeDetails).length === 0) {
    delete next.stripeDetails;
  }
}

function cleanNullableBillingFields(next) {
  if (!isPlainObject(next.billingDetails)) return;
  [
    'plan',
    'entitlementOverrides',
    'coveredCommitment',
    'feeOverride',
    'stripeCustomerId',
    'stripeSubscriptionId',
  ].forEach((key) => removeIfNull(next.billingDetails, key));
}

export function applyDevStateOverrides(rawMerchant, devState) {
  const patch = devState?.merchantPatch;
  if (!rawMerchant || !patch || Object.keys(patch).length === 0) {
    return rawMerchant;
  }

  const next = mergePlainObjects(rawMerchant, patch);
  next.__devStateLabOverrides = true;
  syncOnboardingMirrors(next, patch);
  syncPaymentProcessorMirrors(next, patch);
  cleanNullableBillingFields(next);

  if (patch.finixDetails === null) {
    delete next.finixDetails;
  }

  return next;
}

export function summarizeMerchantState(merchant) {
  if (!merchant) {
    return {
      plan: 'none',
      entitlementOverride: 'none',
      onboardingStep: 'none',
      selectedPlanIntent: 'none',
      processor: 'none',
      stripeAccount: 'none',
      chargesEnabled: 'unknown',
    };
  }

  return {
    plan: merchant.billingDetails?.plan || 'STARTER',
    entitlementOverride: merchant.billingDetails?.entitlementOverrides || 'none',
    onboardingStep:
      merchant.onboardingCurrentStep || merchant.onboardingDetails?.currentStep || 'none',
    selectedPlanIntent:
      merchant.onboardingSelectedPlan || merchant.onboardingDetails?.selectedPlan || 'none',
    processor: merchant.defaultPaymentProcessor || 'unset',
    stripeAccount:
      merchant.stripeAccountID || merchant.stripeDetails?.accountId ? 'present' : 'none',
    chargesEnabled:
      merchant.stripeDetails?.chargesEnabled === true
        ? 'true'
        : merchant.stripeDetails?.chargesEnabled === false
          ? 'false'
          : 'unknown',
  };
}
